// apps/client/src/hooks/office/useLineOfTrailKpiOffice.ts
// OFFICE DASHBOARD SPECIFIC - Year-based filtering only (no relative periods)
// Separated from main dashboard hooks for independent evolution
// MIGRATED (2026-07-10): Fetches through the shared dashboard query cache (useDashboardQuery).

import { useMemo } from "react";
import { useDashboardQuery } from "../useDashboardQuery";

interface UseLineOfTrailKpiOfficeParams {
  companyId: string;
  officeId?: string;
  templateId?: string;
  year: number;
  yearType: 'calendar' | 'financial';
  includeByBroker?: boolean;
  includeByLender?: boolean;
  entityCode?: string;
}

interface BrokerLotData {
  lot: number;
  loanAmount: number;
}

interface LenderLotData {
  lot: number;
  loanAmount: number;
}

interface MonthlyLineOfTrailData {
  month: string;
  lot: number;
  loanAmount: number;
  byBroker?: Record<string, BrokerLotData>;
  byLender?: Record<string, LenderLotData>;
  [key: string]: string | number | Record<string, BrokerLotData> | Record<string, LenderLotData> | undefined;
}

interface UseLineOfTrailKpiOfficeReturn {
  series: MonthlyLineOfTrailData[];
  loading: boolean;
  error: string | null;
}

/**
 * Office Dashboard hook to fetch historical Line of Trail data
 * Uses year-based filtering only (calendar or financial year)
 */
export const useLineOfTrailKpiOffice = ({
  companyId,
  officeId,
  templateId,
  year,
  yearType,
  includeByBroker,
  includeByLender,
  entityCode,
}: UseLineOfTrailKpiOfficeParams): UseLineOfTrailKpiOfficeReturn => {
  const { data, loading, error } = useDashboardQuery<{
    series: Array<{
      month: string;
      lot: number;
      loanAmount: number;
      byBroker?: Record<string, { lot: number; loanAmount: number }>;
      byLender?: Record<string, { lot: number; loanAmount: number }>;
    }>;
  }>(
    'getLineOfTrailGrowth',
    {
      companyId,
      officeId,
      templateId,
      year,
      yearType,
      includeByBroker,
      includeByLender,
      entityCode,
    },
    { enabled: !!companyId && !!year }
  );

  const series = useMemo<MonthlyLineOfTrailData[]>(() => {
    if (!data) return [];

    return data.series.map((item) => {
      const [yearStr, monthStr] = item.month.split('-');
      const monthIndex = parseInt(monthStr, 10) - 1;
      const date = new Date(parseInt(yearStr, 10), monthIndex, 1);
      const monthLabel = date.toLocaleString('default', { month: 'short' });

      return {
        month: monthLabel,
        lot: item.lot,
        loanAmount: item.loanAmount,
        ...(item.byBroker && { byBroker: item.byBroker }),
        ...(item.byLender && { byLender: item.byLender }),
      };
    });
  }, [data]);

  return { series, loading, error };
};
